/**
 * Old tax regime — client-side mirrors of backend/oldRegimeInvestmentCalc.js
 */

export const SELF_OCCUPIED_HP_INTEREST_CAP = 200000;
export const HOUSE_PROPERTY_LOSS_SETOFF_CAP = 200000;
export const MAX_SELF_OCCUPIED_PROPERTIES = 2;
export const MAX_HOUSE_PROPERTIES = 5;

const PREVENTIVE_CAP_PER_CATEGORY = 5000;

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const round2 = (v) => Math.round(num(v) * 100) / 100;

export function occupancyToUiPropertyType(occupancy) {
  const o = String(occupancy || "").toUpperCase().replace(/[-\s]+/g, "_");
  if (o === "LET_OUT") return "let-out";
  if (o === "DEEMED_LET_OUT" || o.includes("DEEMED")) return "deemed-let-out";
  return "self-occupied";
}

export function uiPropertyTypeToOccupancy(propertyType) {
  if (propertyType === "let-out") return "LET_OUT";
  if (propertyType === "deemed-let-out") return "DEEMED_LET_OUT";
  return "SELF_OCCUPIED";
}

export function isRentablePropertyType(propertyType) {
  return propertyType === "let-out" || propertyType === "deemed-let-out";
}

export const medicalCapForCategory = (isSenior) => (isSenior ? 50000 : 25000);

/**
 * HRA exemption u/s 10(13A) — same formula as payrollTdsService.computeHraExemption.
 * Returns amount + intermediate legs for UI preview.
 */
export function computeHraExemptionDetail({
  hraAnnual = 0,
  basicAnnual = 0,
  rentAnnual = 0,
  isMetro = true,
} = {}) {
  const hra = Math.max(0, num(hraAnnual));
  const basic = Math.max(0, num(basicAnnual));
  const rent = Math.max(0, num(rentAnnual));
  if (!(rent > 0)) {
    return {
      exemption: 0,
      actualHra: hra,
      basic,
      rent,
      rentMinus10PctBasic: 0,
      pctOfBasic: round2(basic * (isMetro ? 0.5 : 0.4)),
      metroPct: isMetro ? 50 : 40,
      isMetro: !!isMetro,
    };
  }
  const tenPctBasic = basic * 0.1;
  const rentMinus10PctBasic = round2(Math.max(0, rent - tenPctBasic));
  const metroPct = isMetro ? 50 : 40;
  const pctOfBasic = round2(basic * (metroPct / 100));
  const exemption = round2(Math.min(hra, rentMinus10PctBasic, pctOfBasic));
  return {
    exemption,
    actualHra: hra,
    basic,
    rent,
    rentMinus10PctBasic,
    pctOfBasic,
    metroPct,
    isMetro: !!isMetro,
  };
}

export function capMedicalCategory(premium, preventive, isSenior) {
  const cap = medicalCapForCategory(isSenior);
  const prem = Math.max(0, num(premium));
  const prev = Math.min(PREVENTIVE_CAP_PER_CATEGORY, Math.max(0, num(preventive)));
  return Math.min(cap, prem + prev);
}

export function computeSection80DAllowedFromUi(section80D = {}) {
  const isSelfSenior = !!section80D.isSelfSeniorCitizen;
  const isParentsSenior = !!section80D.isParentsSeniorCitizen;

  const selfAllowed = capMedicalCategory(
    section80D.self,
    section80D.selfPreventiveHealthCheckup ?? section80D.preventiveHealthCheckup,
    isSelfSenior
  );

  const parentsAllowed = capMedicalCategory(
    section80D.parents,
    section80D.parentsPreventiveHealthCheckup,
    isParentsSenior
  );

  return round2(selfAllowed + parentsAllowed);
}

export function computeRentablePropertyBreakdown(entry = {}) {
  const gav = num(entry.grossAnnualValue ?? entry.rentalIncome);
  const municipal = num(entry.municipalTaxes);
  const nav = Math.max(0, gav - municipal);
  const standardDeduction = round2(nav * 0.3);
  const interest = num(entry.interest);
  const netIncome = round2(nav - standardDeduction - interest);
  return { gav, municipal, nav, standardDeduction, interest, netIncome };
}

export function computeHousePropertyAggregateFromUi(entries = []) {
  const rows = Array.isArray(entries) ? entries : [];

  let rentableNetTotal = 0;
  let sopInterestTotal = 0;
  let sopCount = 0;
  const perProperty = [];

  for (const entry of rows) {
    const propertyType = entry.propertyType || "self-occupied";
    const interest = num(entry.interest);

    if (isRentablePropertyType(propertyType)) {
      const breakdown = computeRentablePropertyBreakdown(entry);
      rentableNetTotal += breakdown.netIncome;
      perProperty.push({
        propertyType,
        interest,
        ...breakdown,
      });
    } else {
      sopCount += 1;
      sopInterestTotal += interest;
      perProperty.push({
        propertyType: "self-occupied",
        interest,
        netIncome: 0,
        gav: 0,
        municipal: 0,
        nav: 0,
        standardDeduction: 0,
      });
    }
  }

  const sopAllowedInterest = Math.min(
    sopInterestTotal,
    SELF_OCCUPIED_HP_INTEREST_CAP
  );
  const sopNet = round2(-sopAllowedInterest);
  const rawAggregateNet = round2(rentableNetTotal + sopNet);

  let hpNetForTax = rawAggregateNet;
  let lossSetOffApplied = 0;
  let lossCarriedForward = 0;

  if (rawAggregateNet < 0) {
    lossSetOffApplied = Math.min(
      Math.abs(rawAggregateNet),
      HOUSE_PROPERTY_LOSS_SETOFF_CAP
    );
    hpNetForTax = round2(-lossSetOffApplied);
    lossCarriedForward = round2(Math.abs(rawAggregateNet) - lossSetOffApplied);
  }

  return {
    hpNetForTax,
    rawAggregateNet,
    rentableNetTotal: round2(rentableNetTotal),
    sopCount,
    sopInterestTotal: round2(sopInterestTotal),
    sopAllowedInterest: round2(sopAllowedInterest),
    sopExcessInterest: round2(
      Math.max(0, sopInterestTotal - SELF_OCCUPIED_HP_INTEREST_CAP)
    ),
    lossSetOffApplied: round2(lossSetOffApplied),
    lossCarriedForward: round2(lossCarriedForward),
    perProperty,
  };
}

export function validateHousePropertyEntriesUi(entries = []) {
  const rows = Array.isArray(entries) ? entries : [];
  const sopCount = rows.filter((e) => e.propertyType === "self-occupied").length;
  if (sopCount > MAX_SELF_OCCUPIED_PROPERTIES) {
    return {
      valid: false,
      reason: `At most ${MAX_SELF_OCCUPIED_PROPERTIES} self-occupied properties are allowed. Mark additional homes as Let-Out or Deemed Let-Out.`,
    };
  }
  if (rows.length > MAX_HOUSE_PROPERTIES) {
    return {
      valid: false,
      reason: `At most ${MAX_HOUSE_PROPERTIES} house properties can be declared.`,
    };
  }
  for (let i = 0; i < rows.length; i++) {
    if (isRentablePropertyType(rows[i].propertyType)) {
      const gav = num(rows[i].grossAnnualValue);
      if (gav <= 0) {
        return {
          valid: false,
          reason: `Property ${i + 1}: Gross Annual Value is required for let-out / deemed let-out.`,
        };
      }
    }
  }
  return { valid: true, reason: null };
}

export function computeViaDeductionsTotalOldRegime(sectionVIA = {}) {
  const d = sectionVIA.section80D || {};
  const o = sectionVIA.otherInvestments || {};
  const section80D = computeSection80DAllowedFromUi(d);
  const eduLoan = num(o.section80E);
  const nps = Math.min(50000, num(o.section80CCD1B));
  const ee80 = Math.min(50000, num(o.section80EE));
  const eea80 = Math.min(150000, num(o.section80EEA));
  const eeb80 = Math.min(150000, num(o.section80EEB));
  const savings80TTA = Math.min(10000, num(o.section80TTA));
  return round2(section80D + eduLoan + nps + ee80 + eea80 + eeb80 + savings80TTA);
}

export function formatInrCap(n) {
  return `₹${Number(n).toLocaleString("en-IN")}`;
}

/** @deprecated use computeRentablePropertyBreakdown */
export function computeLetOutHousePropertyBreakdown(entry = {}) {
  return computeRentablePropertyBreakdown(entry);
}
